import { parseQuery } from 'services/QueryParser';
import { CREATE_MEMBERSHIP } from 'services/Permissions';
import { DEFAULT_TOKEN } from 'services/PaginationHelper';
import { LABEL_KEY_ALERTS_DISABLED, GOOD_CELLULAR_CONNECTION_THRESHOLD, isReplaced } from 'services/SensorHelper';
import { States } from '../../app.router';
import _cloneDeep from 'lodash/cloneDeep';
import DeviceTypeFilterController from './device-type-filter-popover/controller';
import DeviceTypeFilterTemplate from './device-type-filter-popover/template.html';
import LabelFilterController from './label-filter-popover/controller';
import LabelFilterTemplate from './label-filter-popover/template.html';
import ProjectLabelFilterController from './project-label-filter-popover/controller';
import ProjectLabelFilterTemplate from './project-label-filter-popover/template.html';
import ProjectFilterController from './project-filter-popover/controller';
import ProjectFilterTemplate from './project-filter-popover/template.html';
import PausedAlertsFilterController from './paused-alerts-filter-popover/controller';
import PausedAlertsFilterTemplate from './paused-alerts-filter-popover/template.html';
import EmulatorFilterController from './emulator-filter-popover/controller';
import EmulatorFilterTemplate from './emulator-filter-popover/template.html';
import ActivationFilterController from './activation-filter-popover/controller';
import ActivationFilterTemplate from './activation-filter-popover/template.html';
import BatteryStateFilterController from './battery-state-filter-popover/controller';
import BatteryStateFilterTemplate from './battery-state-filter-popover/template.html';
import ColumnConfigController from './column-config-popover/controller';
import ColumnConfigTemplate from './column-config-popover/template.html';
import {
    getBatteryStateLabel,
    getBatteryIcon,
    getBatteryStateBadgeClass,
    getBatteryInfoHintClass,
    getBatteryPercentage,
    getBatteryTooltipSentence,
    getEstimatedRemainingYears,
    getEstimatedRemainingYearsDisplay,
} from 'services/BatteryHelper';

/* @ngInject */
export default class OrganizationDevicesController {
    constructor(
        SensorService,
        IAMService,
        Loader,
        ToastService,
        $scope,
        $state,
        ProjectManager,
        RoleManager,
        $q,
        $mdPanel,
        $filter,
        FeatureFlags
    ) {
        this.SensorService = SensorService;
        this.IAMService = IAMService;
        this.Loader = Loader;
        this.ToastService = ToastService;
        this.$scope = $scope;
        this.$state = $state;
        this.$filter = $filter;
        this.ProjectManager = ProjectManager;
        this.RoleManager = RoleManager;
        this.$q = $q;
        this.$mdPanel = $mdPanel;
        this.FeatureFlags = FeatureFlags;

        // Bind methods to preserve 'this' context
        this.onOrderChange = this.onOrderChange.bind(this);
    }


    $onInit() {
        this.stateTitle = 'Organization Devices';

        // Initialize component state
        this.devices = [];
        this.filteredDevices = [];
        this.projects = [];
        this.totalDeviceCount = 0;
        this.loading = true;
        this.query = ''; // Full query with type filters
        this.displayQuery = ''; // Query displayed in search box (without type filters)
        this.previousQuery = '';
        this.orderBy = '';
        this.orderDirection = '';
        this.currentPageSize = 100;
        this.currentPageToken = DEFAULT_TOKEN;
        this.nextPageToken = '';
        this.isFetching = false;

        // Initialize selection state
        this.selectAll = false;
        this.selectedDevices = [];
        this.showWarning = false;

        // Initialize device type filters - all off by default
        this.deviceTypeFilters = [
            { displayName: 'Temperature', value: 'temperature', active: false, count: 0 },
            { displayName: 'Proximity', value: 'proximity', active: false, count: 0 },
            { displayName: 'Touch', value: 'touch', active: false, count: 0 },
            { displayName: 'Humidity', value: 'humidity', active: false, count: 0 },
            { displayName: 'Counting Proximity', value: 'proximityCounter', active: false, count: 0 },
            { displayName: 'Counting Touch', value: 'touchCounter', active: false, count: 0 },
            { displayName: 'Water Detector', value: 'waterDetector', active: false, count: 0 },
            { displayName: 'CO2', value: 'co2', active: false, count: 0 },
            { displayName: 'Motion', value: 'motion', active: false, count: 0 },
            { displayName: 'Desk Occupancy', value: 'deskOccupancy', active: false, count: 0 },
            { displayName: 'Door & Window', value: 'contact', active: false, count: 0 },
            { displayName: 'Differential Pressure', value: 'differentialPressure', active: false, count: 0 },
            { displayName: 'Cloud Connector', value: 'ccon', active: false, count: 0 }
        ];

        // Initialize active filter text
        this.activeDeviceTypeFilterText = null;

        // Initialize project filter
        this.projectFilterMode = 'include'; // 'include' or 'exclude'
        this.activeProjectIds = []; // For include mode
        this.excludedProjectIds = []; // For exclude mode
        this.activeProjectFilterText = null;

        // Initialize device label filters
        this.labelFilters = [{ key: '', value: '' }];
        this.activeLabelFilterText = null;

        // Initialize project label filters
        this.projectLabelFilters = [{ key: '', value: '' }];
        this.activeProjectLabelFilterText = null;

        // Initialize paused alerts filter
        this.pausedAlertsFilter = null; // null = no filter, 'paused' = alerts paused, 'not-paused' = alerts not paused
        this.activePausedAlertsFilterText = null;

        // Initialize emulator filter
        this.emulatorFilter = null; // null = no filter, 'ONLY_EMULATORS' = only emulators, 'ONLY_NON_EMULATORS' = only non-emulators
        this.activeEmulatorFilterText = null;

        // Initialize activation filter
        this.activationFilters = { active: false, deactivated: false, replaced: false };
        this.activeActivationFilterText = null;

        // Initialize battery state filter
        this.batteryStateFilter = null; // null = no filter, 'CRITICAL', 'LOW', 'OK'
        this.activeBatteryStateFilterText = null;

        // Initialize custom label columns
        this.customLabelColumns = []; // Array of label keys to show as columns

        // Initialize export state
        this.isExporting = false;

        // Load initial data
        this.loadProjects().then(() => {
            const { projectLabels } = this.$state.params;
            if (projectLabels) {
                this.updateProjectLabelFilters(Object.entries(projectLabels).map(([key, value]) => ({ key, value })));
            } else {
                this.loadDevices();
            }
        });
    }

    get isOrganizationAdmin() {
        return this.RoleManager.hasOrganizationPermissionTo(CREATE_MEMBERSHIP);
    }

    get organizationId() {
        return this.ProjectManager.currentProject.organizationId;
    }

    get organizationName() {
        return this.ProjectManager.currentProject.organizationDisplayName;
    }

    get goodCellularConnectionThreshold() { // eslint-disable-line class-methods-use-this
        return GOOD_CELLULAR_CONNECTION_THRESHOLD;
    }

    async loadProjects() {
        try {
            // Fetch all pages of projects for the organization
            const allProjects = [];
            let nextPageToken = '';

            do {
                // eslint-disable-next-line no-await-in-loop
                const response = await this.IAMService.projects({
                    organization: this.ProjectManager.currentProject.organization,
                    pageToken: nextPageToken,
                    pageSize: 100
                });

                allProjects.push(...response.data);
                nextPageToken = response.nextPageToken || '';
            } while (nextPageToken);

            this.projects = allProjects;

            // Calculate total device count across all projects
            this.totalDeviceCount = this.projects.reduce((total, project) => {
                return total + (project.sensorCount || 0) + (project.cloudConnectorCount || 0);
            }, 0);
        } catch (error) {
            this.ToastService.showSimpleTranslated('failed_to_load_projects');
            // eslint-disable-next-line no-console
            console.error('Failed to load projects:', error);
        }
    }

    loadDevices() {

        this.loading = true;
        this.isFetching = true;
        this.Loader.show();

        // Build query parameters
        const params = {
            ...parseQuery(this.query),
            pageSize: this.currentPageSize,
            pageToken: this.currentPageToken
        };

        if (this.orderBy) {
            params.orderBy = this.orderBy;
        }

        // Add emulator filter if set
        if (this.emulatorFilter) {
            params.emulatorFilter = this.emulatorFilter;
        }

        // Add activation filter if set
        this.applyActivationFilterParams(params);

        // Add project filter if set
        if (this.projectFilterMode === 'include' && this.activeProjectIds.length > 0) {
            params.projects = this.activeProjectIds.map(id => `projects/${id}`);
        } else if (this.projectFilterMode === 'exclude' && this.excludedProjectIds.length > 0) {
            const includedProjects = this.projects
                .filter(p => !this.excludedProjectIds.includes(p.id))
                .map(p => `projects/${p.id}`);
            if (includedProjects.length > 0) {
                params.projects = includedProjects;
            }
        }

        // Add battery state filter via eventPredicates
        if (this.batteryStateFilter) {
            const state = this.batteryStateFilter;
            params.eventPredicates = [`batteryStatus.state == "${state}"`];
        }

        return this.SensorService.organizationDevices(this.organizationId, params)
            .then(({ data, nextPageToken }) => {
                this.devices = data;
                this.nextPageToken = nextPageToken;
                this.applyFilters();
                this.updateDeviceTypeCounts();
            })
            .catch((error) => {
                this.ToastService.showSimpleTranslated('failed_to_load_devices');
                // eslint-disable-next-line no-console
                console.error('Failed to load devices:', error);
            })
            .finally(() => {
                this.loading = false;
                this.isFetching = false;
                this.Loader.hide();
            });
    }

    applyFilters() {
        // For organization devices, filtering is handled server-side via the API
        // The parseQuery function handles structured queries like "type:temperature", "label:name=value", etc.
        // We only need client-side filtering for simple text searches that aren't structured queries

        let filtered = [...this.devices];

        // Skip client-side filtering when the query is a device ID list — the API already filtered
        // by exact IDs and matching the full "id1,id2" string against individual device IDs would return nothing.
        if (this.query && !this.query.includes(':') && !parseQuery(this.query).deviceIds) {
            filtered = filtered.filter(device => {
                const searchTerm = this.query.toLowerCase();
                const deviceName = (device.labels?.name || '').toLowerCase();
                const deviceId = device.id.toLowerCase();
                return deviceName.includes(searchTerm) || deviceId.includes(searchTerm);
            });
        }

        // Apply "not-paused" filter client-side (since API doesn't support negative label filters)
        if (this.pausedAlertsFilter === 'not-paused') {
            filtered = filtered.filter(device => {
                return !device.labels || !device.labels['dt-alerts-disabled'];
            });
        }

        this.filteredDevices = filtered;
    }

    search(event) {
        // Handle both string queries and event objects from search box
        const userQuery = typeof event === 'string' ? event : (event?.query || '');
        this.displayQuery = userQuery;

        // Build the full query by combining user query with active type filters
        this.query = this.buildFullQuery(userQuery);
        this.currentPageToken = DEFAULT_TOKEN;
        this.loadDevices();
    }

    clearSearchResult() {
        this.displayQuery = '';
        this.search('');
    }

    buildFullQuery(userQuery) {
        const queryParts = [];

        // Add user query if present
        if (userQuery) {
            queryParts.push(userQuery);
        }

        // Add type filters
        const activeTypes = this.deviceTypeFilters.filter(f => f.active).map(f => f.value);
        activeTypes.forEach(type => {
            queryParts.push(`type:${type}`);
        });

        // Add device label filters
        const activeLabels = this.labelFilters.filter(l => l.key && l.key.trim() !== '');
        activeLabels.forEach(label => {
            if (label.value && label.value.trim() !== '') {
                // Key and value: label:key=value (quote value if it contains spaces)
                const value = label.value.includes(' ') ? `"${label.value}"` : label.value;
                queryParts.push(`label:${label.key}=${value}`);
            } else {
                // Key only: label:key
                queryParts.push(`label:${label.key}`);
            }
        });

        // Add project label filters (using same label: prefix as device labels)
        const activeProjectLabels = this.projectLabelFilters.filter(l => l.key && l.key.trim() !== '');
        activeProjectLabels.forEach(label => {
            if (label.value && label.value.trim() !== '') {
                // Key and value: label:key=value (quote value if it contains spaces)
                const value = label.value.includes(' ') ? `"${label.value}"` : label.value;
                queryParts.push(`label:${label.key}=${value}`);
            } else {
                // Key only: label:key
                queryParts.push(`label:${label.key}`);
            }
        });

        // Add paused alerts filter
        if (this.pausedAlertsFilter === 'paused') {
            queryParts.push('label:dt-alerts-disabled');
        } else if (this.pausedAlertsFilter === 'not-paused') {
            // For "not paused", we need to exclude devices with the label
            // This is handled client-side in applyFilters() since the API doesn't support negative label filters
        }

        return queryParts.join(' ');
    }

    get hasNoDevices() {
        // Only show "no devices" message if we're not loading, have no devices, AND no filters are active
        return !this.loading && this.devices.length === 0 && !this.isFetching && !this.hasActiveFilters;
    }

    get hasNoMatchingDevices() {
        // Show "no matching devices" if we have no results but filters ARE active
        return !this.loading && !this.isFetching && this.filteredDevices.length === 0 && this.hasActiveFilters;
    }

    get hasActiveFilters() {
        // Check if any filters are active
        return this.activeDeviceTypeFilterText ||
               this.activeLabelFilterText ||
               this.activeProjectLabelFilterText ||
               this.activePausedAlertsFilterText ||
               this.activeEmulatorFilterText ||
               this.activeActivationFilterText ||
               this.activeProjectFilterText ||
               this.activeBatteryStateFilterText ||
               (this.displayQuery && this.displayQuery.trim() !== '');
    }

    get hasCustomColumns() {
        return this.customLabelColumns && this.customLabelColumns.length > 0;
    }

    toggleSelectAll() {
        this.devices.forEach(device => {
            device.selected = this.selectAll;
        });
        this.updateSelectedDevices();
    }

    updateSelectedDevices() {
        this.selectedDevices = this.devices.filter(device => device.selected);
    }

    setPageToken({ pageToken }) {
        this.currentPageToken = pageToken;
        this.loadDevices();
    }

    setPageSize({ pageSize }) {
        this.currentPageSize = pageSize;
        this.currentPageToken = DEFAULT_TOKEN;
        this.loadDevices();
    }

    onOrderChange(order) {
        this.orderBy = order;
        this.currentPageToken = DEFAULT_TOKEN;
        this.loadDevices();
    }

    getProjectName(device) {
        // Extract project ID from device resource name (e.g., "projects/abc/devices/xyz" -> "abc")
        const projectId = device.name ? device.name.split('/')[1] : null;
        if (!projectId) {
            return 'Unknown Project';
        }

        const project = this.projects.find(p => p.id === projectId);
        return project ? project.displayName : projectId;
    }

    showDeviceTypeFilter() {
        const button = document.getElementById('deviceTypeFilterButton');
        const position = this.$mdPanel.newPanelPosition()
            .relativeTo(button)
            .addPanelPosition(this.$mdPanel.xPosition.ALIGN_START, this.$mdPanel.yPosition.BELOW)
            .withOffsetX('280px').withOffsetY('4px');

        const panelAnimation = this.$mdPanel.newPanelAnimation()
            .openFrom(button)
            .withAnimation('md-panel--animation');

        const config = {
            attachTo: document.body,
            controller: DeviceTypeFilterController,
            controllerAs: '$ctrl',
            template: DeviceTypeFilterTemplate,
            panelClass: 'md-panel-container',
            animation: panelAnimation,
            position,
            openFrom: button,
            clickOutsideToClose: true,
            escapeToClose: true,
            onRemoving: this.closeDeviceTypeFilterPopover.bind(this),
            focusOnOpen: true,
            zIndex: 100,
            locals: {
                filters: _cloneDeep(this.deviceTypeFilters),
                onUpdateFilters: this.updateDeviceTypeFilters.bind(this),
                onClose: this.closeDeviceTypeFilterPopover.bind(this)
            }
        };

        this.$mdPanel.open(config).then(reference => {
            this.deviceTypeFilterPanelRef = reference;
        });
    }

    updateDeviceTypeFilters(filters) {
        this.deviceTypeFilters = filters;

        // Build query string with active device types
        const activeFilters = filters.filter(f => f.active);

        // Update the active filter text for display
        if (activeFilters.length === 0) {
            this.activeDeviceTypeFilterText = null;
        } else if (activeFilters.length === 1) {
            this.activeDeviceTypeFilterText = activeFilters[0].displayName;
        } else {
            this.activeDeviceTypeFilterText = `${activeFilters.length} device types`;
        }

        // Rebuild the full query with the user's search and new type filters
        this.query = this.buildFullQuery(this.displayQuery);

        // Reload devices with the new query
        this.currentPageToken = DEFAULT_TOKEN;
        this.loadDevices();
    }

    clearDeviceTypeFilter() {
        this.deviceTypeFilters.forEach(filter => {
            filter.active = false;
        });
        this.activeDeviceTypeFilterText = null;
        this.query = this.buildFullQuery(this.displayQuery);
        this.currentPageToken = DEFAULT_TOKEN;
        this.loadDevices();
    }

    closeDeviceTypeFilterPopover() {
        if (this.deviceTypeFilterPanelRef) {
            this.deviceTypeFilterPanelRef.close();
            this.deviceTypeFilterPanelRef = null;
        }
    }

    showLabelFilter() {
        const button = document.getElementById('labelFilterButton');
        const position = this.$mdPanel.newPanelPosition()
            .relativeTo(button)
            .addPanelPosition(this.$mdPanel.xPosition.ALIGN_START, this.$mdPanel.yPosition.BELOW)
            .withOffsetX('280px').withOffsetY('4px');

        const panelAnimation = this.$mdPanel.newPanelAnimation()
            .openFrom(button)
            .withAnimation('md-panel--animation');

        const config = {
            attachTo: document.body,
            controller: LabelFilterController,
            controllerAs: '$ctrl',
            template: LabelFilterTemplate,
            panelClass: 'md-panel-container',
            animation: panelAnimation,
            position,
            openFrom: button,
            clickOutsideToClose: true,
            escapeToClose: true,
            onRemoving: this.closeLabelFilterPopover.bind(this),
            focusOnOpen: true,
            zIndex: 100,
            locals: {
                labels: _cloneDeep(this.labelFilters),
                onUpdateLabels: this.updateLabelFilters.bind(this),
                onClose: this.closeLabelFilterPopover.bind(this),
                SensorService: this.SensorService
            }
        };

        this.$mdPanel.open(config).then(reference => {
            this.labelFilterPanelRef = reference;
        });
    }

    updateLabelFilters(labels) {
        this.labelFilters = labels;

        // Count active label filters (with non-empty keys)
        const activeLabels = labels.filter(l => l.key && l.key.trim() !== '');

        // Update the active filter text for display
        if (activeLabels.length === 0) {
            this.activeLabelFilterText = null;
        } else if (activeLabels.length === 1) {
            const label = activeLabels[0];
            this.activeLabelFilterText = label.value ? `${label.key}=${label.value}` : label.key;
        } else {
            this.activeLabelFilterText = `${activeLabels.length} labels`;
        }

        // Rebuild the full query with the user's search and new label filters
        this.query = this.buildFullQuery(this.displayQuery);

        // Reload devices with the new query
        this.currentPageToken = DEFAULT_TOKEN;
        this.loadDevices();
    }

    clearLabelFilter() {
        this.labelFilters = [{ key: '', value: '' }];
        this.activeLabelFilterText = null;
        this.query = this.buildFullQuery(this.displayQuery);
        this.currentPageToken = DEFAULT_TOKEN;
        this.loadDevices();
    }

    closeLabelFilterPopover() {
        if (this.labelFilterPanelRef) {
            this.labelFilterPanelRef.close();
            this.labelFilterPanelRef = null;
        }
    }

    showPausedAlertsFilter() {
        const button = document.getElementById('pausedAlertsFilterButton');

        const position = this.$mdPanel.newPanelPosition()
            .relativeTo(button)
            .addPanelPosition(this.$mdPanel.xPosition.OFFSET_END, this.$mdPanel.yPosition.BELOW)
            .withOffsetY('4px');

        const panelAnimation = this.$mdPanel.newPanelAnimation()
            .openFrom(button)
            .withAnimation('md-panel--animation');

        const config = {
            attachTo: document.body,
            controller: PausedAlertsFilterController,
            controllerAs: '$ctrl',
            template: PausedAlertsFilterTemplate,
            panelClass: 'md-panel-container',
            animation: panelAnimation,
            position,
            openFrom: button,
            clickOutsideToClose: true,
            escapeToClose: true,
            onRemoving: this.closePausedAlertsFilterPopover.bind(this),
            focusOnOpen: true,
            zIndex: 100,
            locals: {
                currentFilter: this.pausedAlertsFilter,
                onUpdate: this.updatePausedAlertsFilter.bind(this),
                onClose: this.closePausedAlertsFilterPopover.bind(this)
            }
        };

        this.$mdPanel.open(config).then(reference => {
            this.pausedAlertsFilterPanelRef = reference;
        });
    }

    updatePausedAlertsFilter(filter) {
        this.pausedAlertsFilter = filter;

        // Update the active filter text for display
        if (!filter) {
            this.activePausedAlertsFilterText = null;
        } else if (filter === 'paused') {
            this.activePausedAlertsFilterText = 'Alerts paused';
        } else if (filter === 'not-paused') {
            this.activePausedAlertsFilterText = 'Alerts not paused';
        }

        // Rebuild the full query with the new filter
        this.query = this.buildFullQuery(this.displayQuery);

        // Reload devices with the new query
        this.currentPageToken = DEFAULT_TOKEN;
        this.loadDevices();
    }

    closePausedAlertsFilterPopover() {
        if (this.pausedAlertsFilterPanelRef) {
            this.pausedAlertsFilterPanelRef.close();
            this.pausedAlertsFilterPanelRef = null;
        }
    }

    showProjectLabelFilter() {
        const button = document.getElementById('projectLabelFilterButton');

        const position = this.$mdPanel.newPanelPosition()
            .relativeTo(button)
            .addPanelPosition(this.$mdPanel.xPosition.ALIGN_START, this.$mdPanel.yPosition.BELOW)
            .withOffsetX('280px').withOffsetY('4px');

        const panelAnimation = this.$mdPanel.newPanelAnimation()
            .openFrom(button)
            .withAnimation('md-panel--animation');

        const config = {
            attachTo: document.body,
            controller: ProjectLabelFilterController,
            controllerAs: '$ctrl',
            template: ProjectLabelFilterTemplate,
            panelClass: 'md-panel-container',
            animation: panelAnimation,
            position,
            openFrom: button,
            clickOutsideToClose: true,
            escapeToClose: true,
            onRemoving: this.closeProjectLabelFilterPopover.bind(this),
            focusOnOpen: true,
            zIndex: 100,
            locals: {
                labels: _cloneDeep(this.projectLabelFilters),
                SensorService: this.SensorService,
                onUpdateLabels: this.updateProjectLabelFilters.bind(this),
                onClose: this.closeProjectLabelFilterPopover.bind(this)
            }
        };

        this.$mdPanel.open(config).then(reference => {
            this.projectLabelFilterPanelRef = reference;
        });
    }

    updateProjectLabelFilters(labels) {
        this.projectLabelFilters = labels.length > 0 ? labels : [{ key: '', value: '' }];

        // Update the active filter text for display
        const activeProjectLabels = this.projectLabelFilters.filter(l => l.key && l.key.trim() !== '');
        if (activeProjectLabels.length === 0) {
            this.activeProjectLabelFilterText = null;
        } else if (activeProjectLabels.length === 1) {
            const label = activeProjectLabels[0];
            if (label.value && label.value.trim() !== '') {
                this.activeProjectLabelFilterText = `${label.key}=${label.value}`;
            } else {
                this.activeProjectLabelFilterText = label.key;
            }
        } else {
            this.activeProjectLabelFilterText = `${activeProjectLabels.length} project labels`;
        }

        // Rebuild the full query with the new filters
        this.query = this.buildFullQuery(this.displayQuery);

        // Reload devices with the new query
        this.currentPageToken = DEFAULT_TOKEN;
        this.loadDevices();
    }

    closeProjectLabelFilterPopover() {
        if (this.projectLabelFilterPanelRef) {
            this.projectLabelFilterPanelRef.close();
            this.projectLabelFilterPanelRef = null;
        }
    }

    clearProjectLabelFilter() {
        this.projectLabelFilters = [{ key: '', value: '' }];
        this.activeProjectLabelFilterText = null;
        this.query = this.buildFullQuery(this.displayQuery);
        this.currentPageToken = DEFAULT_TOKEN;
        this.loadDevices();
    }

    clearPausedAlertsFilter() {
        this.pausedAlertsFilter = null;
        this.activePausedAlertsFilterText = null;
        this.query = this.buildFullQuery(this.displayQuery);
        this.currentPageToken = DEFAULT_TOKEN;
        this.loadDevices();
    }

    showProjectFilter() {
        const button = document.getElementById('projectFilterButton');

        const position = this.$mdPanel.newPanelPosition()
            .relativeTo(button)
            .addPanelPosition(this.$mdPanel.xPosition.ALIGN_START, this.$mdPanel.yPosition.BELOW)
            .withOffsetX('400px').withOffsetY('4px');

        const panelAnimation = this.$mdPanel.newPanelAnimation()
            .openFrom(button)
            .withAnimation('md-panel--animation');

        const config = {
            attachTo: document.body,
            controller: ProjectFilterController,
            controllerAs: '$ctrl',
            template: ProjectFilterTemplate,
            panelClass: 'md-panel-container',
            animation: panelAnimation,
            position,
            openFrom: button,
            clickOutsideToClose: true,
            escapeToClose: true,
            onRemoving: this.closeProjectFilterPopover.bind(this),
            focusOnOpen: true,
            zIndex: 100,
            locals: {
                projects: this.projects,
                activeProjects: this.activeProjectIds,
                excludedProjects: this.excludedProjectIds,
                filterMode: this.projectFilterMode,
                onUpdate: this.updateProjectFilter.bind(this),
                onClose: this.closeProjectFilterPopover.bind(this)
            }
        };

        this.$mdPanel.open(config).then(reference => {
            this.projectFilterPanelRef = reference;
        });
    }

    updateProjectFilter({ mode, activeProjects, excludedProjects }) {
        this.projectFilterMode = mode;
        this.activeProjectIds = activeProjects;
        this.excludedProjectIds = excludedProjects;

        // Update the active filter text for display
        if (mode === 'include') {
            if (activeProjects.length === 0) {
                this.activeProjectFilterText = null;
            } else if (activeProjects.length === 1) {
                const project = this.projects.find(p => p.id === activeProjects[0]);
                this.activeProjectFilterText = project ? project.displayName : activeProjects[0];
            } else {
                this.activeProjectFilterText = `${activeProjects.length} projects`;
            }
        } else if (excludedProjects.length === 0) {
            // Exclude mode - no exclusions
            this.activeProjectFilterText = null;
        } else if (excludedProjects.length === 1) {
            // Exclude mode - single exclusion
            const project = this.projects.find(p => p.id === excludedProjects[0]);
            const projectName = project ? project.displayName : excludedProjects[0];
            this.activeProjectFilterText = `All except ${projectName}`;
        } else {
            // Exclude mode - multiple exclusions
            this.activeProjectFilterText = `All except ${excludedProjects.length}`;
        }

        // Reload devices with the new filter
        this.currentPageToken = DEFAULT_TOKEN;
        this.loadDevices();
    }

    closeProjectFilterPopover() {
        if (this.projectFilterPanelRef) {
            this.projectFilterPanelRef.close();
            this.projectFilterPanelRef = null;
        }
    }

    clearProjectFilter() {
        this.projectFilterMode = 'include';
        this.activeProjectIds = [];
        this.excludedProjectIds = [];
        this.activeProjectFilterText = null;
        this.currentPageToken = DEFAULT_TOKEN;
        this.loadDevices();
    }

    showEmulatorFilter() {
        const button = document.getElementById('emulatorFilterButton');

        const position = this.$mdPanel.newPanelPosition()
            .relativeTo(button)
            .addPanelPosition(this.$mdPanel.xPosition.OFFSET_END, this.$mdPanel.yPosition.BELOW)
            .withOffsetY('4px');

        const panelAnimation = this.$mdPanel.newPanelAnimation()
            .openFrom(button)
            .withAnimation('md-panel--animation');

        const config = {
            attachTo: document.body,
            controller: EmulatorFilterController,
            controllerAs: '$ctrl',
            template: EmulatorFilterTemplate,
            panelClass: 'md-panel-container',
            animation: panelAnimation,
            position,
            openFrom: button,
            clickOutsideToClose: true,
            escapeToClose: true,
            onRemoving: this.closeEmulatorFilterPopover.bind(this),
            focusOnOpen: true,
            zIndex: 100,
            locals: {
                currentFilter: this.emulatorFilter,
                onUpdate: this.updateEmulatorFilter.bind(this),
                onClose: this.closeEmulatorFilterPopover.bind(this)
            }
        };

        this.$mdPanel.open(config).then(reference => {
            this.emulatorFilterPanelRef = reference;
        });
    }

    updateEmulatorFilter(filter) {
        this.emulatorFilter = filter;

        // Update the active filter text for display
        if (!filter) {
            this.activeEmulatorFilterText = null;
        } else if (filter === 'ONLY_EMULATORS') {
            this.activeEmulatorFilterText = 'Only Emulators';
        } else if (filter === 'ONLY_NON_EMULATORS') {
            this.activeEmulatorFilterText = 'Only Non-Emulators';
        }

        // Reload devices with the new emulator filter
        this.currentPageToken = DEFAULT_TOKEN;
        this.loadDevices();
    }

    closeEmulatorFilterPopover() {
        if (this.emulatorFilterPanelRef) {
            this.emulatorFilterPanelRef.close();
            this.emulatorFilterPanelRef = null;
        }
    }

    clearEmulatorFilter() {
        this.emulatorFilter = null;
        this.activeEmulatorFilterText = null;
        this.currentPageToken = DEFAULT_TOKEN;
        this.loadDevices();
    }

    showActivationFilter() {
        const button = document.getElementById('activationFilterButton');

        const position = this.$mdPanel.newPanelPosition()
            .relativeTo(button)
            .addPanelPosition(this.$mdPanel.xPosition.OFFSET_END, this.$mdPanel.yPosition.BELOW)
            .withOffsetY('4px');

        const panelAnimation = this.$mdPanel.newPanelAnimation()
            .openFrom(button)
            .withAnimation('md-panel--animation');

        const config = {
            attachTo: document.body,
            controller: ActivationFilterController,
            controllerAs: '$ctrl',
            template: ActivationFilterTemplate,
            panelClass: 'md-panel-container',
            animation: panelAnimation,
            position,
            openFrom: button,
            clickOutsideToClose: true,
            escapeToClose: true,
            onRemoving: this.closeActivationFilterPopover.bind(this),
            focusOnOpen: true,
            zIndex: 100,
            locals: {
                currentFilters: this.activationFilters,
                onUpdate: this.updateActivationFilter.bind(this),
                onClose: this.closeActivationFilterPopover.bind(this)
            }
        };

        this.$mdPanel.open(config).then(reference => {
            this.activationFilterPanelRef = reference;
        });
    }

    updateActivationFilter(filters) {
        this.activationFilters = filters;
        const { active, deactivated, replaced } = filters;
        if (!active && !deactivated && !replaced || active && deactivated && replaced) {
            this.activeActivationFilterText = null;
        } else if (active && !deactivated && !replaced) {
            this.activeActivationFilterText = 'Active';
        } else if (!active && deactivated && !replaced) {
            this.activeActivationFilterText = 'Deactivated';
        } else if (!active && !deactivated && replaced) {
            this.activeActivationFilterText = 'Replaced';
        } else if (!active && deactivated && replaced) {
            this.activeActivationFilterText = 'Deactivated & Replaced';
        } else if (active && deactivated && !replaced) {
            this.activeActivationFilterText = 'Active & Deactivated';
        }

        this.currentPageToken = DEFAULT_TOKEN;
        this.loadDevices();
    }

    closeActivationFilterPopover() {
        if (this.activationFilterPanelRef) {
            this.activationFilterPanelRef.close();
            this.activationFilterPanelRef = null;
        }
    }

    clearActivationFilter() {
        this.activationFilters = { active: false, deactivated: false, replaced: false };
        this.activeActivationFilterText = null;
        this.currentPageToken = DEFAULT_TOKEN;
        this.loadDevices();
    }

    showBatteryStateFilter() {
        const button = document.getElementById('batteryStateFilterButton');

        const position = this.$mdPanel.newPanelPosition()
            .relativeTo(button)
            .addPanelPosition(this.$mdPanel.xPosition.OFFSET_END, this.$mdPanel.yPosition.BELOW)
            .withOffsetY('4px');

        const panelAnimation = this.$mdPanel.newPanelAnimation()
            .openFrom(button)
            .withAnimation('md-panel--animation');

        const config = {
            attachTo: document.body,
            controller: BatteryStateFilterController,
            controllerAs: '$ctrl',
            template: BatteryStateFilterTemplate,
            panelClass: 'md-panel-container',
            animation: panelAnimation,
            position,
            openFrom: button,
            clickOutsideToClose: true,
            escapeToClose: true,
            onRemoving: this.closeBatteryStateFilterPopover.bind(this),
            focusOnOpen: true,
            zIndex: 100,
            locals: {
                currentFilter: this.batteryStateFilter,
                onUpdate: this.updateBatteryStateFilter.bind(this),
                onClose: this.closeBatteryStateFilterPopover.bind(this)
            }
        };

        this.$mdPanel.open(config).then(reference => {
            this.batteryStateFilterPanelRef = reference;
        });
    }

    updateBatteryStateFilter(filter) {
        this.batteryStateFilter = filter;

        if (!filter) {
            this.activeBatteryStateFilterText = null;
        } else if (filter === 'CRITICAL') {
            this.activeBatteryStateFilterText = 'Critical';
        } else if (filter === 'LOW') {
            this.activeBatteryStateFilterText = 'Low';
        } else if (filter === 'OK') {
            this.activeBatteryStateFilterText = 'OK';
        }

        this.currentPageToken = DEFAULT_TOKEN;
        this.loadDevices();
    }

    closeBatteryStateFilterPopover() {
        if (this.batteryStateFilterPanelRef) {
            this.batteryStateFilterPanelRef.close();
            this.batteryStateFilterPanelRef = null;
        }
    }

    clearBatteryStateFilter() {
        this.batteryStateFilter = null;
        this.activeBatteryStateFilterText = null;
        this.currentPageToken = DEFAULT_TOKEN;
        this.loadDevices();
    }

    applyActivationFilterParams(params) {
        const { active, deactivated, replaced } = this.activationFilters;
        if (!active && !deactivated && !replaced) return;
        if (active && deactivated && replaced) return;

        if (active && !deactivated && !replaced) {
            params.activationFilter = 'ONLY_ACTIVATED_DEVICES';
        } else if (!active && deactivated && !replaced) {
            params.activationFilter = 'ONLY_DEACTIVATED_DEVICES';
            params.labelFilters = [...(params.labelFilters || []), '-dt-replacement-time'];
        } else if (!active && !deactivated && replaced) {
            // No activationFilter needed — dt-replacement-time label implies deactivated,
            // and using ONLY_DEACTIVATED_DEVICES can exclude replaced emulators.
            params.labelFilters = [...(params.labelFilters || []), 'dt-replacement-time'];
        } else if (!active && deactivated && replaced) {
            params.activationFilter = 'ONLY_DEACTIVATED_DEVICES';
        } else if (active && deactivated && !replaced) {
            params.labelFilters = [...(params.labelFilters || []), '-dt-replacement-time'];
        }
    }

    updateDeviceTypeCounts() {
        // Reset all counts
        this.deviceTypeFilters.forEach(filter => {
            filter.count = 0;
        });

        // Count devices by type
        this.devices.forEach(device => {
            const filter = this.deviceTypeFilters.find(f => f.value === device.type);
            if (filter) {
                filter.count++;
            }
        });
    }

    navigateToDevice(device, $event) {
        // Don't navigate if clicking on checkbox or other interactive elements
        const target = $event.target;
        if (target.tagName === 'MD-CHECKBOX' ||
            target.type === 'checkbox' ||
            target.closest('md-checkbox') ||
            target.closest('.md-cell-checkbox')) {
            $event.stopPropagation();
            return;
        }

        // Extract project ID from device resource name (e.g., "projects/abc/devices/xyz" -> "abc")
        const projectId = device.name ? device.name.split('/')[1] : null;

        if (!projectId) {
            // eslint-disable-next-line no-console
            console.error('Could not extract project ID from device name:', device.name);
            return;
        }

        // Navigate to device details page in the project
        // Both sensors and cloud connectors use the same state (SENSOR_DETAILS)
        this.$state.go(States.SENSOR_DETAILS, {
            projectId: projectId,
            sensorId: device.id
        });
    }

    alertsDisabled(device) { // eslint-disable-line class-methods-use-this
        return device.labels && device.labels[LABEL_KEY_ALERTS_DISABLED] !== undefined;
    }

    isReplaced(device) { // eslint-disable-line class-methods-use-this
        return isReplaced(device);
    }

    getBatteryStateLabel(device) { return getBatteryStateLabel(device); } // eslint-disable-line class-methods-use-this

    getBatteryIcon(device) { return getBatteryIcon(device); } // eslint-disable-line class-methods-use-this

    getBatteryStateBadgeClass(device) { return getBatteryStateBadgeClass(device); } // eslint-disable-line class-methods-use-this

    getBatteryInfoHintClass(device) { return getBatteryInfoHintClass(device); } // eslint-disable-line class-methods-use-this

    getBatteryPercentage(device) { return getBatteryPercentage(device); } // eslint-disable-line class-methods-use-this

    getBatteryTooltipSentence(device) { return getBatteryTooltipSentence(device); } // eslint-disable-line class-methods-use-this

    getEstimatedRemainingYears(device) { return getEstimatedRemainingYears(device); } // eslint-disable-line class-methods-use-this

    getEstimatedRemainingYearsDisplay(device) { return getEstimatedRemainingYearsDisplay(device); } // eslint-disable-line class-methods-use-this

    showColumnConfig() {
        const button = document.getElementById('columnConfigButton');

        const position = this.$mdPanel.newPanelPosition()
            .relativeTo(button)
            .addPanelPosition(this.$mdPanel.xPosition.OFFSET_END, this.$mdPanel.yPosition.BELOW)
            .withOffsetY('4px');

        const panelAnimation = this.$mdPanel.newPanelAnimation()
            .openFrom(button)
            .withAnimation('md-panel--animation');

        const config = {
            attachTo: document.body,
            controller: ColumnConfigController,
            controllerAs: '$ctrl',
            template: ColumnConfigTemplate,
            panelClass: 'md-panel-container',
            animation: panelAnimation,
            position,
            openFrom: button,
            clickOutsideToClose: true,
            escapeToClose: true,
            onRemoving: this.closeColumnConfigPopover.bind(this),
            focusOnOpen: true,
            zIndex: 100,
            locals: {
                currentColumns: _cloneDeep(this.customLabelColumns),
                SensorService: this.SensorService,
                onUpdate: this.updateCustomColumns.bind(this),
                onClose: this.closeColumnConfigPopover.bind(this)
            }
        };

        this.$mdPanel.open(config).then(reference => {
            this.columnConfigPanelRef = reference;
        });
    }

    updateCustomColumns(columns) {
        this.customLabelColumns = columns;
        this.$scope.$applyAsync();
    }

    closeColumnConfigPopover() {
        if (this.columnConfigPanelRef) {
            this.columnConfigPanelRef.close();
            this.columnConfigPanelRef = null;
        }
    }

    async fetchAllDevices() {
        // Build query parameters with current filters
        const params = {
            ...parseQuery(this.query),
            pageSize: 1000, // Use larger page size for export
            pageToken: DEFAULT_TOKEN
        };

        // Add orderBy if we have a sort order
        if (this.orderBy) {
            params.orderBy = this.orderBy;
        }

        // Add emulator filter if set
        if (this.emulatorFilter) {
            params.emulatorFilter = this.emulatorFilter;
        }

        // Add activation filter if set
        this.applyActivationFilterParams(params);

        // Add project filter if set
        if (this.projectFilterMode === 'include' && this.activeProjectIds.length > 0) {
            params.projects = this.activeProjectIds.map(id => `projects/${id}`);
        } else if (this.projectFilterMode === 'exclude' && this.excludedProjectIds.length > 0) {
            // For exclude mode, include all projects except the excluded ones
            const includedProjects = this.projects
                .filter(p => !this.excludedProjectIds.includes(p.id))
                .map(p => `projects/${p.id}`);
            if (includedProjects.length > 0) {
                params.projects = includedProjects;
            }
        }

        // Add battery state filter via eventPredicates
        if (this.batteryStateFilter) {
            const state = this.batteryStateFilter;
            params.eventPredicates = [`batteryStatus.state == "${state}"`];
        }

        const allDevices = [];
        let nextPageToken = DEFAULT_TOKEN;

        try {
            // Fetch all pages
            do {
                params.pageToken = nextPageToken;
                // eslint-disable-next-line no-await-in-loop
                const { data, nextPageToken: newNextPageToken } = await this.SensorService.organizationDevices(this.organizationId, params);
                allDevices.push(...data);
                nextPageToken = newNextPageToken;
            } while (nextPageToken);

            // Apply client-side filters (for 'not-paused' alerts which can't be filtered server-side)
            let filtered = [...allDevices];

            // Skip client-side filtering when the query is a device ID list — the API already filtered
            // by exact IDs and matching the full "id1,id2" string against individual device IDs would return nothing.
            if (this.query && !this.query.includes(':') && !parseQuery(this.query).deviceIds) {
                // Simple text search - filter client-side for device names and IDs
                filtered = filtered.filter(device => {
                    const searchTerm = this.query.toLowerCase();
                    const deviceName = (device.labels?.name || '').toLowerCase();
                    const deviceId = device.id.toLowerCase();
                    return deviceName.includes(searchTerm) || deviceId.includes(searchTerm);
                });
            }

            // Client-side filter for "not-paused" alerts (API doesn't support negative label filters)
            if (this.pausedAlertsFilter === 'not-paused') {
                filtered = filtered.filter(device => !this.alertsDisabled(device));
            }

            return filtered;
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to fetch devices for export:', error);
            this.ToastService.showSimpleTranslated('failed_to_export_devices');
            return null;
        }
    }

    async exportDeviceIds() {
        this.isExporting = true;
        this.$scope.$applyAsync();

        const devices = await this.fetchAllDevices();

        this.isExporting = false;
        this.$scope.$applyAsync();

        if (!devices || devices.length === 0) {
            this.ToastService.showSimpleTranslated('no_devices_to_export');
            return;
        }

        // Create CSV content with just device IDs
        const csvContent = `Device ID\n${devices.map(device => device.id).join('\n')}`;

        // Create and download the file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `device-ids-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        this.ToastService.showSimple(`Exported ${devices.length} device ID${devices.length === 1 ? '' : 's'}`);
    }

    async exportAllColumns() {
        this.isExporting = true;
        this.$scope.$applyAsync();

        const devices = await this.fetchAllDevices();

        this.isExporting = false;
        this.$scope.$applyAsync();

        if (!devices || devices.length === 0) {
            this.ToastService.showSimpleTranslated('no_devices_to_export');
            return;
        }

        // Create CSV content with all columns including custom label columns
        const headers = [
            'Device ID', 'Name', 'Type', 'Project', 'State',
            'Battery Status', 'Battery %', 'Est. Remaining Lifetime (years)',
            ...this.customLabelColumns.map(key => key),
            'Last Seen', 'Active', 'Emulated', 'Alerts Paused'
        ];
        const csvRows = [headers.join(',')];

        devices.forEach(device => {
            // Extract nested ternary logic for state value
            let stateValue = device.stateValue;
            if (!stateValue) {
                if (device.type === 'ccon') {
                    stateValue = device.offline ? 'Offline' : 'Online';
                } else {
                    stateValue = 'No data';
                }
            }

            // Extract nested ternary logic for last seen
            let lastSeenValue;
            if (device.lastSeen) {
                lastSeenValue = `"${this.$filter('date')(device.lastSeen, 'MMM d, y HH:mm')}"`;
            } else if (device.type === 'ccon') {
                lastSeenValue = '"– –"';
            } else {
                lastSeenValue = '"Never"';
            }

            const batteryStateLabel = this.getBatteryStateLabel(device) || '';
            const batteryPct = this.getBatteryPercentage(device);
            const estYears = this.getEstimatedRemainingYears(device);

            const row = [
                `"${device.id}"`,
                `"${device.labels?.name || ''}"`,
                `"${device.typeName || ''}"`,
                `"${this.getProjectName(device)}"`,
                `"${stateValue}"`,
                `"${batteryStateLabel}"`,
                batteryPct !== null ? batteryPct : '',
                estYears !== null ? estYears : '',
                ...this.customLabelColumns.map(key => {
                    const value = device.labels?.[key];
                    return value !== undefined ? `"${value}"` : '""';
                }),
                lastSeenValue,
                device.active ? 'TRUE' : 'FALSE',
                device.id.startsWith('emu') ? 'TRUE' : 'FALSE',
                this.alertsDisabled(device) ? 'TRUE' : 'FALSE'
            ];
            csvRows.push(row.join(','));
        });

        const csvContent = csvRows.join('\n');

        // Create and download the file
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `devices-export-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        this.ToastService.showSimple(`Exported ${devices.length} device${devices.length === 1 ? '' : 's'}`);
    }
}
